/**
 * The shapes the tellraw composer works in.
 *
 * A message is kept as a flat list of styled runs rather than as Minecraft's
 * own nested text component, for two reasons. The assistant (in Mr. Roboto)
 * answers in exactly this shape under a strict JSON schema, so what it can say
 * is what the editor can hold. And a run can carry intent the game's format
 * cannot, like "this word is a gold-to-orange gradient", which only becomes
 * per-character colours when it is compiled for the versions a schedule
 * targets. Keeping the intent is what lets a gradient come out as real hex on
 * modern servers today and still step cleanly if 1.12 support is switched on
 * tomorrow.
 *
 * `TextRun`, `HoverRun`, `TellrawMessage` and `Compat` are part of the contract
 * shared with argus-backend and Mr. Roboto (`/scheduled-dispatch/compose`,
 * a schedule's `editor_state`, the tellraw library). Change them there first.
 */

/** Minecraft's sixteen named chat colours, in colour-code order (§0 to §f). */
export const NAMED_COLORS = [
  'black',
  'dark_blue',
  'dark_green',
  'dark_aqua',
  'dark_red',
  'dark_purple',
  'gold',
  'gray',
  'dark_gray',
  'blue',
  'green',
  'aqua',
  'red',
  'light_purple',
  'yellow',
  'white',
] as const;

export type NamedColor = (typeof NAMED_COLORS)[number];

export const CLICK_ACTIONS = ['open_url', 'run_command', 'suggest_command', 'copy_to_clipboard'] as const;

export type ClickAction = (typeof CLICK_ACTIONS)[number];

export interface ClickEvent {
  action: ClickAction;
  value: string;
}

export interface RunStyle {
  /** One of the sixteen named colours or `#rrggbb`. Null leaves the chat default (white). */
  color: string | null;
  /** Two to six `#rrggbb` stops spread across the run's text. When set, `color` is null. */
  gradient: string[] | null;
  bold: boolean;
  italic: boolean;
  underlined: boolean;
  strikethrough: boolean;
  obfuscated: boolean;
}

/** Text shown in a hover tooltip. It cannot itself be clicked or hovered. */
export interface HoverRun extends RunStyle {
  text: string;
}

export interface TextRun extends RunStyle {
  text: string;
  click: ClickEvent | null;
  hover: { runs: HoverRun[] } | null;
}

/**
 * A component imported from an existing tellraw that the run model cannot
 * express (a scoreboard value, a selector, a translation key, a keybind, NBT).
 * It is sent exactly as it was written, and a message holding one is not
 * offered to the assistant, which could not give it back unchanged.
 */
export interface RawRun {
  raw: Record<string, unknown>;
}

export type Run = TextRun | RawRun;

export interface TellrawMessage {
  runs: Run[];
}

/**
 * Which version families the compiled command must work on. At least one is on.
 *
 * - `legacy`: 1.15 and older. Named colours only; no hex.
 * - `modern`: 1.16 to 1.21.4. Hex colours; `clickEvent` / `hoverEvent`.
 * - `current`: 1.21.5 and newer. Text components are SNBT; `click_event` / `hover_event`.
 */
export interface Compat {
  legacy: boolean;
  modern: boolean;
  current: boolean;
}

export type VersionFamily = keyof Compat;

/** A JSON text component as the compiler emits it. */
export type JsonComponent = Record<string, unknown>;

export type CompileNoteKind =
  /** A hex colour was replaced by its nearest named colour because 1.15 and older is on. */
  | 'hex_to_named'
  /** A gradient was stepped through named colours because 1.15 and older is on. */
  | 'gradient_stepped'
  /** A copy-to-clipboard click does nothing on 1.14 and older. */
  | 'copy_legacy'
  /** Click and hover events carry both spellings so one command reaches every family that is on. */
  | 'dual_events'
  /** An imported component the run model cannot express was kept as it was. */
  | 'raw_kept'
  /** The command is longer than one RCON packet, so servers reached through the RCON fallback cannot run it. */
  | 'rcon_length';

export interface CompileNote {
  kind: CompileNoteKind;
  /** One plain sentence for staff, e.g. "The gradient on “SALE” steps through gold, yellow and white." */
  message: string;
}

export interface CompileResult {
  /** The whole console command, e.g. `tellraw @a {"text":"","extra":[…]}`. */
  command: string;
  /** The component alone, as compact JSON. */
  json: string;
  component: JsonComponent;
  /** What was changed or cannot work for the families that are on, deduplicated. */
  notes: CompileNote[];
  /** UTF-8 length of `command`. */
  bytes: number;
}

/** One run of text as a player would see it, with inheritance already resolved. */
export interface PreviewSpan {
  text: string;
  /** Resolved `#rrggbb`. */
  color: string;
  bold: boolean;
  italic: boolean;
  underlined: boolean;
  strikethrough: boolean;
  obfuscated: boolean;
  click: ClickEvent | null;
  hover: PreviewSpan[] | null;
  /** Stand-in text for a component only the server can fill in (selector, score, keybind, translation, NBT). */
  placeholder: boolean;
}

export type ParseResult =
  | {
      ok: true;
      /** The target from `tellraw <target> …`, or null when bare JSON was given. */
      selector: string | null;
      /** The component exactly as parsed. */
      component: unknown;
      message: TellrawMessage;
      /** False when the message holds a RawRun, so the assistant is not offered. */
      editable: boolean;
    }
  | {
      ok: false;
      /** One plain sentence, e.g. "Expected a comma or a closing brace." */
      error: string;
      /** 1-based position of the problem in the input, when known. */
      line?: number;
      column?: number;
    };

export interface MessageIssue {
  /** Index of the run the issue is in, or null for the message as a whole. */
  run: number | null;
  message: string;
}
